/** tm/components/CollectStepConfigPanel.tsx — 자료수집 스텝 설정 패널 (TemplatesScreen 구조 호환) */
import { useState, useRef, useEffect } from 'react';
import { BrandPicker, brandLabel } from '../../brand';
import type { CollectStepConfig, CollectItem, CollectItemGuide } from '../types';
import { C, T, R, IMEText } from './ui';
import * as collectApi from '../../api';
import BundleLinkModal from './BundleLinkModal';

interface CollectTemplate { id: string; name: string; itemCount: number }
interface Props {
  /** 로고 고르기 숨김 — 늘 로움인 곳(대출 점검 · 2026-10-29 지도사 「로움 · 올댓 묻지 마 · 모두 로움」) */
  hideBrand?: boolean;
  /** 맨 아래 시작 단추 글(기본 「▶ 자료수집 시작 (고객에게 전송)」) */
  startLabel?: string;
  value: CollectStepConfig;
  onChange: (v: CollectStepConfig) => void;
  onToast?: (msg: string) => void;
  projectId?: string;
  onProjectLinked?: (projectId: string) => void;
  // V22_COLLECT_START:22-Aa — 자료수집 시작 콜백 (있으면 패널 하단에 버튼 표시)
  onStartCollect?: () => void;
  // URL복사 시 포털이 없으면 생성하고 pid 반환
  onEnsurePortal?: () => Promise<string | null>;
  // 이 업체 저장공간에 이미 있는 자료 (중복 요청 방지 안내)
  storageFiles?: { id: string; file_name: string }[];
}

interface MyProject { id: string; name: string; project?: string; company_name?: string; created_at?: string; status?: string; createdAt?: string }

const lbl: React.CSSProperties = { fontSize: T.xs, fontWeight: 600, color: C.sub, marginBottom: 4 };
const inp: React.CSSProperties = { width: '100%', padding: '7px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', boxSizing: 'border-box' as const };
const rowS: React.CSSProperties = { display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' };   // 좁은 칸에서 줄바꿈 — 항목명 칸이 0 폭으로 찌그러지던 것(2026-10-29)
const secTitle: React.CSSProperties = { fontSize: T.md, fontWeight: 700, color: C.text, marginBottom: 8, marginTop: 16 };
const addBtn: React.CSSProperties = { padding: '6px 14px', borderRadius: R.sm, border: `1px dashed ${C.border}`, background: C.white, color: C.accent, fontSize: T.xs, fontWeight: 600, cursor: 'pointer' };
const delBtn: React.CSSProperties = { padding: '2px 6px', borderRadius: R.sm, border: 'none', background: C.redLt, color: C.red, fontSize: T.xs, cursor: 'pointer', flexShrink: 0 };
const togBase: React.CSSProperties = { padding: '3px 8px', borderRadius: R.sm, fontSize: T.xs, fontWeight: 600, cursor: 'pointer', border: 'none', whiteSpace: 'nowrap' as const };

function auth(): Record<string, string> {
  return { Authorization: 'Bearer ' + (localStorage.getItem('token') || ''), 'Content-Type': 'application/json' };
}
function uid() { return Math.random().toString(36).slice(2, 8); }
/* 자주 쓰는 항목 모음 (2026-10-12 지도사 「자주 쓰는 항목은 자동 채우기 · 가이드 파일 · 링크도 자동으로」) — 서버 /api/templates/item-library */
export interface LibItem { label: string; type: string; description: string; required: boolean; sensitive: boolean; options: string[]; guides: CollectItemGuide[]; uses: number }
export const libKey = (s?: string) => (s || '').replace(/\s/g, '').toLowerCase();
let libCache: Promise<LibItem[]> | null = null;
export function loadLib(): Promise<LibItem[]> {
  if (!libCache) {
    libCache = fetch('/api/templates/item-library', { headers: auth(), cache: 'no-store' })
      .then(r => r.json()).then((j: { success?: boolean; data?: LibItem[] }) => (j && j.success && Array.isArray(j.data) ? j.data : []))
      .catch(() => { libCache = null; return []; });
  }
  return libCache;
}
const LIB_TYPES: CollectItem['type'][] = ['file', 'text', 'text_long', 'radio', 'checkbox'];
function libGuideText(g: CollectItemGuide[]): string {
  const l = g.filter(x => x.type === 'link').length, f = g.filter(x => x.type === 'file').length;
  return [l ? `링크 ${l}` : '', f ? `파일 ${f}` : ''].filter(Boolean).join(' · ');
}

function emptyItem(): CollectItem {
  return { id: uid(), label: '', type: 'file', required: true, sensitive: false, description: '', section: '', guides: [], options: [] };
}
function needsOptions(t: string) { return t === 'radio' || t === 'checkbox'; }

export default function CollectStepConfigPanel({ hideBrand, startLabel, value, onChange, onToast, projectId, onProjectLinked, onStartCollect, onEnsurePortal, storageFiles }: Props) { // V22_COLLECT_START:22-Ab
  const [newLink, setNewLink] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saveMode, setSaveMode] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [collectTemplates, setCollectTemplates] = useState<CollectTemplate[]>([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [bundlePid, setBundlePid] = useState<string | null>(null);
  const [myProjects, setMyProjects] = useState<MyProject[]>([]);
  const [linkSearch, setLinkSearch] = useState('');
  const [linking, setLinking] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dropIdx, setDropIdx] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const guideFileRef = useRef<HTMLInputElement>(null);
  const guideTarget = useRef<{ itemIdx: number } | null>(null);

  const items = value.items || [];
  const hasStored = (label?: string) => {
    const n = (label || '').replace(/\s/g, '').toLowerCase();
    if (!n || !storageFiles) return false;
    return storageFiles.some(f => { const fn = (f.file_name || '').replace(/\.[^.]+$/, '').replace(/\s/g, '').toLowerCase(); return !!fn && (fn.indexOf(n) >= 0 || n.indexOf(fn) >= 0); });
  };
  const refFiles = value.refFiles || [];
  const refLinks = value.refLinks || [];
  const [lib, setLib] = useState<LibItem[]>([]);
  const [fillSeq, setFillSeq] = useState(0);   // 자동 채움 뒤 설명 · 섹션 칸(입력은 비제어)을 새 값으로 다시 그림
  useEffect(() => { loadLib().then(setLib); }, []);
  const libBy = new Map(lib.map(x => [libKey(x.label), x]));
  /** 모음의 항목으로 채움 — 이미 적어 둔 설명 · 가이드는 그대로 두고 빈 칸만 · 섹션은 모음에서 안 옴(회사마다 다름 · 2026-10-28) */
  function filledFrom(it: CollectItem, L: LibItem): CollectItem {
    const type = (LIB_TYPES.includes(L.type as CollectItem['type']) ? L.type : 'file') as CollectItem['type'];
    return {
      ...it, label: L.label, type, required: L.required, sensitive: L.sensitive,
      description: it.description || L.description || '', section: it.section || '',
      guides: (it.guides || []).length ? it.guides : L.guides.map(g => ({ ...g })),
      options: needsOptions(type) && !(it.options || []).filter(Boolean).length ? (L.options.length ? L.options.slice() : ['']) : it.options,
    };
  }
  function applyLib(idx: number, L: LibItem) {
    setItems(items.map((x, i) => i === idx ? filledFrom(x, L) : x));
    setFillSeq(n => n + 1);
    const gt = libGuideText(L.guides);
    if (onToast) onToast(`「${L.label}」 예전 설정으로 채웠어요${gt ? ` · 가이드 ${gt}` : ''}`);
  }
  useEffect(() => {
    collectApi.getTemplates()
      .then((data: Record<string,unknown>[]) => {
        const list = (Array.isArray(data) ? data : []).map(t => ({ id: String(t.id||''), name: String(t.name||''), itemCount: Number(t.itemCount||t.item_count||0) }));
        setCollectTemplates(list);
      })
      .catch(() => {});
  }, []);

  // ── item helpers ──
  function setItems(next: CollectItem[]) { onChange({ ...value, items: next }); }
  function updateItem(idx: number, patch: Partial<CollectItem>) {
    setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  }
  function removeItem(idx: number) { setItems(items.filter((_, i) => i !== idx)); }
  function duplicateItem(idx: number) { // 항목 복사 — 바로 아래 동일 항목 (가이드·설명글·선택지 포함, 사용자 확정)
    const src = items[idx];
    const copy: CollectItem = { ...src, id: uid(), guides: (src.guides || []).map((g) => ({ ...g })), options: (src.options || []).slice() };
    const next = items.slice(); next.splice(idx + 1, 0, copy); setItems(next);
  }
  function addItem() { setItems([...items, emptyItem()]); }

  // ── 프로젝트 연결 ──
  async function openLinkModal() {
    setLinking(true);
    try {
      // 페이지네이션 누적 호출 — 모든 프로젝트 표시
      const all: MyProject[] = [];
      const PAGE_SIZE = 200;
      for (let pg = 1; pg <= 20; pg++) {
        // V22_HOTFIX1:link — request()의 auto-unwrap으로 res가 배열 직접일 수 있음
        const res = await collectApi.getProjects('', 'all', pg, PAGE_SIZE) as MyProject[] | { projects: MyProject[] };
        const arr: MyProject[] = Array.isArray(res) ? res : (Array.isArray((res as { projects?: MyProject[] })?.projects) ? (res as { projects: MyProject[] }).projects : []);
        if (arr.length === 0) break;
        all.push(...arr);
        if (arr.length < PAGE_SIZE) break;
      }
      setMyProjects(all);
      setLinkOpen(true);
    } catch { onToast?.('프로젝트 목록 조회 실패'); }
    setLinking(false);
  }
  async function doLinkProject(pid: string) {
    const okMsg = '기존에 입력된 자료수집 항목이 삭제되고,\n선택한 프로젝트의 항목으로 대체됩니다.\n\n계속하시겠습니까?';
    if (!window.confirm(okMsg)) return;
    setLinking(true);
    try {
      type LinkItem = { id?: string; label: string; type: string; required?: boolean; description?: string; section?: string; options?: string[]; guides?: unknown[] };
      const res = await collectApi.getProject(pid) as { data?: { items?: LinkItem[] }; items?: LinkItem[] };
      const proj = (res && res.data) ? res.data : res; // request() auto-unwrap 대응 — res가 프로젝트 객체 자체일 수 있음
      const projItems: LinkItem[] = Array.isArray(proj?.items) ? proj.items : [];
      const validTypes = ['file','text','text_long','radio','checkbox'] as const;
      type CollectItemType = typeof validTypes[number];
      const normType = (t: string): CollectItemType =>
        (validTypes as readonly string[]).includes(t) ? (t as CollectItemType) : 'file';
      const mapped: CollectItem[] = projItems.map((it) => ({
        id: String(it.id || uid()),
        label: String(it.label || ''),
        type: normType(String(it.type || 'file')),
        required: it.required !== false,
        sensitive: false,
        description: String(it.description || ''),
        section: String(it.section || ''),
        options: Array.isArray(it.options) ? it.options : [],
        guides: (Array.isArray(it.guides) ? it.guides : []) as CollectItemGuide[],
      }));
      onChange({ ...value, items: mapped });
      onProjectLinked?.(pid);
      setLinkOpen(false);
      setLinkSearch('');
      onToast?.('프로젝트가 연결되었습니다');
    } catch { onToast?.('프로젝트 연결 실패'); }
    setLinking(false);
  }

  // ── guide helpers ──
  function updateGuides(itemIdx: number, guides: CollectItemGuide[]) {
    updateItem(itemIdx, { guides });
  }
  function addGuideLink(itemIdx: number) {
    const g = items[itemIdx].guides || [];
    if (g.filter(x => x.type === 'link').length >= 2) return;
    updateGuides(itemIdx, [...g, { type: 'link', label: '', url: '', fileName: '', s3Key: '' }]);
  }
  function addGuideFile(itemIdx: number) {
    const g = items[itemIdx].guides || [];
    if (g.filter(x => x.type === 'file').length >= 2) return;
    guideTarget.current = { itemIdx };
    guideFileRef.current?.click();
  }
  function handleGuideFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f || !guideTarget.current) return;
    const { itemIdx } = guideTarget.current;
    const fd = new FormData();
    fd.append('file', f);
    fetch('/api/templates/upload-guide-file', {
      method: 'POST', headers: { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') }, body: fd,
    })
      .then(r => r.json())
      .then(json => {
        if (json.success) {
          const g = [...(items[itemIdx]?.guides || [])];
          g.push({ type: 'file', label: json.fileName || f.name, url: '', fileName: json.fileName || f.name, s3Key: json.s3Key || '' });
          updateGuides(itemIdx, g);
        }
      })
      .catch(() => {});
    e.target.value = '';
  }
  function removeGuide(itemIdx: number, gi: number) {
    updateGuides(itemIdx, (items[itemIdx].guides || []).filter((_, i) => i !== gi));
  }
  function updateGuide(itemIdx: number, gi: number, patch: Partial<CollectItemGuide>) {
    const g = (items[itemIdx].guides || []).map((x, i) => i === gi ? { ...x, ...patch } : x);
    updateGuides(itemIdx, g);
  }

  // ── 템플릿 간편수정 (사용자 확정): 불러온 구성과 달라지면 [템플릿에 반영] 버튼 노출 ──
  const [tplSnapshot, setTplSnapshot] = useState('');
  const [tplSyncBusy, setTplSyncBusy] = useState(false);
  const [tplAsked, setTplAsked] = useState(false); // '이번 건만' 선택 시 세션 내 재질문 안 함
  function normForDiff(list: CollectItem[]): string {
    return JSON.stringify((list || []).map(it => ({
      l: (it.label || '').trim(), t: it.type, r: it.required !== false, s: !!it.sensitive,
      sec: it.section || '', d: (it.description || '').trim(),
      o: (it.options || []).filter(x => x && x.trim()),
      g: (it.guides || []).map(g => ({ t: g.type, l: (g.label || '').trim(), u: (g.url || '').trim(), f: g.fileName || '' })),
    })));
  }
  async function syncToTemplate() {
    const tplId = value.baseTemplateId || '';
    const tpl = collectTemplates.find(t => t.id === tplId);
    if (!tplId || !tpl) return;
    setTplSyncBusy(true);
    try {
      const payload = {
        name: tpl.name, // PUT은 name 필수 — 미전달 시 이름이 지워짐
        items: items.map(it => ({
          label: it.label, type: it.type, required: it.required !== false, sensitive: !!it.sensitive,
          desc: it.description || '', section: it.section || null,
          options: (it.options || []).filter(x => x && x.trim()),
          guides: (it.guides || []).map(g => ({ type: g.type, label: g.label || '', url: g.url || '', fileName: g.fileName || '', s3Key: g.s3Key || '', filePath: g.s3Key || '' })),
        })),
      };
      const r = await fetch('/api/templates/' + tplId, { method: 'PUT', headers: { ...auth(), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const j = await r.json();
      if (j && j.success !== false) { setTplSnapshot(normForDiff(items)); setTplAsked(false); alertToast('템플릿 "' + tpl.name + '" 업데이트 완료'); }
      else alertToast((j && j.error) || '템플릿 반영 실패');
    } catch (e) { alertToast('템플릿 반영 실패: ' + (e as Error).message); }
    setTplSyncBusy(false);
  }
  function alertToast(msg: string) { if (onToast) onToast(msg); else window.alert(msg); }

  function handleTemplateChange(tplId: string) {
    setTplAsked(false); // 템플릿을 바꿔 고르면 다시 묻는다
    if (!tplId) {
      // V20_MASTER:9 — 템플릿 해제 시 항목까지 같이 제거 여부 확인
      if (value.items && value.items.length > 0) {
        if (!window.confirm('템플릿을 해제하면 자동으로 추가된 수집 항목이 모두 제거됩니다. 계속하시겠습니까?')) return;
      }
      onChange({ ...value, baseTemplateId: '', items: [] });
      return;
    }
    onChange({ ...value, baseTemplateId: tplId });
    fetch('/api/templates/' + tplId, { headers: auth() })
      .then(r => r.json())
      .then(data => {
        if (data && Array.isArray(data.items)) {
          const newItems: CollectItem[] = data.items.map((it: Record<string, unknown>) => ({
            id: uid(),
            label: (it.label as string) || '',
            type: (['file', 'text', 'text_long', 'radio', 'checkbox'].includes(it.type as string) ? it.type : 'file') as CollectItem['type'],
            required: it.required !== false,
            sensitive: it.sensitive === true,
            description: (it.description as string) || (it.desc as string) || '',
            section: (it.section as string) || '',
            options: Array.isArray(it.options) ? (it.options as string[]) : [],
            guides: Array.isArray(it.guides) ? (it.guides as Record<string, unknown>[]).map(g => ({
              type: (g.type as string) === 'file' ? 'file' as const : 'link' as const,
              label: (g.label as string) || '',
              url: (g.url as string) || '',
              fileName: (g.fileName as string) || (g.file_name as string) || '',
              s3Key: (g.s3Key as string) || (g.filePath as string) || (g.file_path as string) || '',
            })) : [],
          }));
          onChange({ ...value, baseTemplateId: tplId, items: newItems });
          setTplSnapshot(normForDiff(newItems)); // 간편수정 diff 기준
        }
      })
      .catch(() => {});
  }

  // ── ref helpers ──
  function addRefLink() {
    if (!newLink.trim()) return;
    onChange({ ...value, refLinks: [...refLinks, newLink.trim()] });
    setNewLink('');
  }
  function removeRefLink(idx: number) { onChange({ ...value, refLinks: refLinks.filter((_, i) => i !== idx) }); }
  function handleRefFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    fetch('/api/tm/upload-file', { method: 'POST', headers: { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') }, body: fd })
      .then(r => r.json())
      .then(json => { if (json.data) onChange({ ...value, refFiles: [...refFiles, { name: json.data.name || f.name, s3_key: json.data.s3_key }] }); })
      .catch(() => {});
    e.target.value = '';
  }
  function removeRefFile(idx: number) { onChange({ ...value, refFiles: refFiles.filter((_, i) => i !== idx) }); }

  // ── save template ──
  function handleSaveTemplate() {
    if (!saveName.trim()) return;
    fetch('/api/templates', {
      method: 'POST', headers: auth(),
      body: JSON.stringify({
        name: saveName.trim(),
        items: items.map(it => ({
          label: it.label, type: it.type, required: it.required,
          sensitive: it.sensitive, desc: it.description, section: it.section || null,
          options: it.options || [],
          guides: it.guides.map(g => ({
            type: g.type, label: g.label, url: g.url,
            fileName: g.fileName, filePath: g.s3Key, s3Key: g.s3Key,
          })),
        })),
      }),
    })
      .then(r => r.json())
      .then(() => {
        setSaveMode(false); setSaveName('');
        onToast?.('템플릿이 저장되었습니다');
        fetch('/api/templates', { headers: auth() })
          .then(r => r.json())
          .then(data => { if (Array.isArray(data)) setCollectTemplates(data); })
          .catch(() => {});
      })
      .catch(() => { onToast?.('템플릿 저장 실패'); });
  }

  // ── drag & drop ──
  function handleDragStart(idx: number) { setDragIdx(idx); }
  function handleDragOver(e: React.DragEvent, idx: number) { e.preventDefault(); setDropIdx(idx); }
  function handleDragEnd() {
    if (dragIdx !== null && dropIdx !== null && dragIdx !== dropIdx) {
      const next = [...items];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(dropIdx, 0, moved);
      setItems(next);
    }
    setDragIdx(null); setDropIdx(null);
  }

  return (
    <div style={{ minWidth: 0, maxWidth: '100%' }}>
      <input ref={guideFileRef} type="file" style={{ display: 'none' }} onChange={handleGuideFileUpload} />

      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
        <div style={secTitle}>수집 항목</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>   {/* 좁은 칸(대출 카드 · 휴대폰)에서 줄바꿈(2026-10-29 — 단추가 세로로 찌그러짐) */}
          {/* 고객 제출 화면 우측 상단 로고 — 설정에 저장하고, 포털이 이미 있으면 바로 반영 (2026-09-11) */}
          {!hideBrand && <BrandPicker size="sm" label="로고" value={value.brand} onChange={(v) => {
            onChange({ ...value, brand: v });
            if (projectId) {
              fetch('/api/projects/' + projectId, { method: 'PUT', headers: { ...auth(), 'Content-Type': 'application/json' }, body: JSON.stringify({ brand: v }) })
                .then((r) => { if (!r.ok) throw new Error(); onToast?.('고객 화면 로고: ' + brandLabel(v)); })
                .catch(() => onToast?.('로고 변경 실패'));
            }
          }} />}
          <button onClick={async () => {
            let pid = projectId;
            if (!pid && onEnsurePortal) pid = (await onEnsurePortal()) || '';
            if (!pid) { alertToast('자료수집 항목을 먼저 추가해 주세요.'); return; }   // 브라우저 경고창 대신 알림(2026-10-14)
            setBundlePid(pid);
          }}
            style={{ fontSize: T.xs, color: C.sub, border: `1px solid ${C.border}`, background: C.white, borderRadius: R.sm, padding: '4px 10px', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
            🔗 URL 복사
          </button>
          <button onClick={() => {
            if (projectId) {
              const url = (window.location.origin || 'https://roum-partners.com') + '/portal/' + projectId;
              window.open(url, '_blank');
            } else {
              setPreviewOpen(true);
            }
          }}
            style={{ fontSize: T.xs, color: C.accent, border: `1px solid ${C.accent}`, background: C.white, borderRadius: R.sm, padding: '4px 10px', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
            포탈 미리보기
          </button>
        </div>
      </div>

      {/* 이미 받은 자료 안내 (중복 요청 방지) */}
      {storageFiles && storageFiles.length > 0 && (
        <div style={{ marginTop: 8, marginBottom: 8, padding: '8px 10px', borderRadius: R.sm, background: '#F0F9FF', border: '1px solid #BAE6FD' }}>
          <div style={{ fontSize: T.xs, fontWeight: 700, color: '#0369A1', marginBottom: 4 }}>📁 이 업체에 이미 받은 자료 ({storageFiles.length}건)</div>
          <div style={{ fontSize: T.xs, color: '#075985', lineHeight: 1.6 }}>{storageFiles.map(f => f.file_name).join(' · ')}</div>
          <div style={{ fontSize: 10, color: '#0EA5E9', marginTop: 4 }}>중복 요청하지 않도록 참고하세요.</div>
        </div>
      )}

      {/* 기존 프로젝트 연결 (드문 기능 — 조용히 표시, 연결 후엔 숨김) */}
      {!projectId && (
        <div style={{ marginTop: 4, marginBottom: 10 }}>
          <span onClick={openLinkModal}
            style={{ fontSize: T.xs, color: C.muted, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}
            title="자료수집 모듈에서 이미 만든 프로젝트를 이 단계에 연결합니다">
            기존 자료수집 프로젝트 연결
          </span>
        </div>
      )}

      {/* 템플릿 수정 반영 확인 — 불러온 구성과 달라지면 능동적으로 묻는다 */}
      {!!value.baseTemplateId && !!tplSnapshot && !tplAsked && normForDiff(items) !== tplSnapshot && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: R.sm, padding: '9px 12px', marginBottom: 10 }}>
          <span style={{ fontSize: T.xs, color: '#065F46', fontWeight: 600, flex: 1, minWidth: 180 }}>
            템플릿 「{(collectTemplates.find(t => t.id === value.baseTemplateId) || { name: '' }).name}」과 구성이 달라졌습니다. 템플릿도 함께 수정할까요?
          </span>
          <button onClick={syncToTemplate} disabled={tplSyncBusy}
            style={{ fontSize: T.xs, color: '#fff', border: 'none', background: '#047857', borderRadius: R.sm, padding: '7px 14px', cursor: 'pointer', fontWeight: 700 }}>
            {tplSyncBusy ? '반영 중…' : '템플릿도 수정'}
          </button>
          <button onClick={() => setTplAsked(true)} disabled={tplSyncBusy}
            style={{ fontSize: T.xs, color: '#065F46', border: '1px solid #A7F3D0', background: '#fff', borderRadius: R.sm, padding: '5px 12px', cursor: 'pointer', fontWeight: 600 }}>
            이번 건만
          </button>
        </div>
      )}

        {/* 베이스 템플릿 */}
        <div style={lbl}>베이스 템플릿</div>
      <select value={value.baseTemplateId || ''} onChange={e => handleTemplateChange(e.target.value)}
        style={{ ...inp, marginBottom: 12 }}>
        <option value="">템플릿 선택 안 함</option>
        {collectTemplates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.itemCount}항목)</option>)}
      </select>

      {/* ── 항목 목록 ── */}
      <datalist id="csc-item-lib">{lib.map(x => { const gt = libGuideText(x.guides); return <option key={x.label} value={x.label} label={`${gt ? gt + ' · ' : ''}${x.uses}번 씀`} />; })}</datalist>
      <datalist id="csc-section-list">{Array.from(new Set(items.map(x => x.section).filter(Boolean))).map(s => <option key={s} value={s} />)}</datalist>
      {items.map((it, idx) => (
        <div key={it.id} draggable onDragStart={() => handleDragStart(idx)} onDragOver={e => handleDragOver(e, idx)} onDragEnd={handleDragEnd}
          style={{
            padding: 10, borderRadius: R.md, marginBottom: 8, background: C.bgAlt,
            border: `1px solid ${dropIdx === idx && dragIdx !== null && dragIdx !== idx ? C.accent : C.border}`,
            borderTop: dropIdx === idx && dragIdx !== null && dragIdx > idx ? `3px solid ${C.accent}` : undefined,
            borderBottom: dropIdx === idx && dragIdx !== null && dragIdx < idx ? `3px solid ${C.accent}` : undefined,
            opacity: dragIdx === idx ? 0.4 : 1,
          }}>
          {/* row 1: label, type, sensitive, required, delete */}
          <div style={rowS}>
            <span style={{ cursor: 'grab', fontSize: T.md, color: C.muted, flexShrink: 0, userSelect: 'none' }}>⠿</span>
            <input key={it.id + '_label_' + fillSeq} defaultValue={it.label} list="csc-item-lib" onChange={e => { const L = libBy.get(libKey(e.target.value)); if (L && libKey(it.label) !== libKey(L.label) && (!(e.nativeEvent as InputEvent).inputType || (e.nativeEvent as InputEvent).inputType === 'insertReplacementText')) applyLib(idx, L); }} onBlur={e => { const L = libBy.get(libKey(e.target.value)); if (L && libKey(it.label) !== libKey(e.target.value) && !(it.guides || []).length && !(it.description || '').trim()) { applyLib(idx, L); return; } if (e.target.value !== it.label) updateItem(idx, { label: e.target.value }); }} onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} placeholder="항목명" style={{ ...inp, flex: '1 1 180px', minWidth: 0, width: 'auto' }} />
            {hasStored(it.label) && <span title="이미 보유 중인 자료 — 중복 요청 주의" style={{ fontSize: 9, fontWeight: 700, color: '#B45309', background: '#FEF3C7', padding: '2px 6px', borderRadius: 8, flexShrink: 0, whiteSpace: 'nowrap' }}>⚠️ 보유</span>}
            <select value={it.type} onChange={e => {
                const newType = e.target.value as CollectItem['type'];
                const patch: Partial<CollectItem> = { type: newType };
                if (needsOptions(newType) && !(it.options && it.options.length > 0)) {
                  patch.options = [''];
                }
                updateItem(idx, patch);
              }}
              style={{ ...inp, width: 90, flex: 'none' }}>
              <option value="file">파일</option>
              <option value="text">단문</option>
              <option value="text_long">장문</option>
              <option value="radio">◉ 객관식</option>
              <option value="checkbox">☑ 다중선택</option>
            </select>
            <button onClick={() => updateItem(idx, { sensitive: !it.sensitive })}
              style={{ ...togBase, background: it.sensitive ? '#D97706' : '#F3F4F6', color: it.sensitive ? '#fff' : '#6B7280' }}>
              {it.sensitive ? '민감 ✓' : '일반'}
            </button>
            <button onClick={() => updateItem(idx, { required: !it.required })}
              style={{ ...togBase, background: it.required ? C.accent : '#F3F4F6', color: it.required ? '#fff' : '#6B7280' }}>
              {it.required ? '필수 ✓' : '선택'}
            </button>
            <button title="항목 복사 — 바로 아래에 동일 항목 생성" onClick={() => duplicateItem(idx)} style={{ ...togBase, background: '#F3F4F6', color: '#6B7280' }}>⧉ 복사</button>
            <button onClick={() => removeItem(idx)} style={delBtn}>삭제</button>
          </div>

          {/* row 1.5: section */}
          <input key={it.id + '_section_' + fillSeq} defaultValue={it.section || ''} onBlur={e => { if (e.target.value !== (it.section || '')) updateItem(idx, { section: e.target.value }); }} list="csc-section-list"
            placeholder="📁 섹션명 (선택 · 같은 이름끼리 포털에서 묶음)"
            style={{ ...inp, width: '100%', marginBottom: 6, color: it.section ? C.accent : undefined, fontWeight: it.section ? 600 : undefined }} />

          {/* row 2: description */}
          <textarea key={it.id + '_desc_' + fillSeq} defaultValue={it.description || ''} onBlur={e => { if (e.target.value !== (it.description || '')) updateItem(idx, { description: e.target.value }); }}
            placeholder="설명글 (선택)" rows={2}
            style={{ ...inp, resize: 'vertical' as const, marginBottom: 6, fontFamily: 'inherit' }} />

          {/* guides */}
          {(it.guides || []).map((g, gi) => (
            <div key={gi} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4, paddingLeft: 4 }}>
              {g.type === 'link' ? (
                <>
                  <span style={{ fontSize: T.xs, color: C.accent, flexShrink: 0 }}>🔗</span>
                  <input value={g.label} onChange={e => updateGuide(idx, gi, { label: e.target.value })}
                    placeholder="링크 이름" style={{ ...inp, width: 100, flex: 'none' }} />
                  <input value={g.url} onChange={e => updateGuide(idx, gi, { url: e.target.value })}
                    placeholder="https://..." style={{ ...inp, flex: 1 }} />
                </>
              ) : (
                <>
                  <span style={{ fontSize: T.xs, color: C.sub, flexShrink: 0 }}>📎</span>
                  <IMEText value={g.label || ''} onChange={v => updateGuide(idx, gi, { label: v })}
                    placeholder={g.fileName || '표시 파일명'} style={{ ...inp, flex: 1, fontSize: T.xs }} />
                  {g.fileName && <span style={{ fontSize: T.xs, color: C.muted, flexShrink: 0, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.fileName}</span>}
                </>
              )}
              <button onClick={() => removeGuide(idx, gi)} style={delBtn}>삭제</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
            {(it.guides || []).filter(g => g.type === 'link').length < 2 && (
              <button onClick={() => addGuideLink(idx)}
                style={{ ...addBtn, padding: '3px 10px', fontSize: T.xs }}>+ 가이드 링크</button>
            )}
            {(it.guides || []).filter(g => g.type === 'file').length < 2 && (
              <button onClick={() => addGuideFile(idx)}
                style={{ ...addBtn, padding: '3px 10px', fontSize: T.xs }}>+ 가이드 파일</button>
            )}
          </div>

          {/* 객관식/다중선택 선택지 UI */}
          {needsOptions(it.type) && (
            <div style={{ marginTop: 8, padding: 8, borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white }}>
              <div style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, marginBottom: 4 }}>
                {it.type === 'radio' ? '◉ 객관식 선택지' : '☑ 다중선택 선택지'}
              </div>
              {(it.options || []).map((opt, oi) => (
                <div key={oi} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: T.xs, color: C.muted, flexShrink: 0 }}>
                    {it.type === 'radio' ? '○' : '□'}
                  </span>
                  <input value={opt} onChange={e => {
                      const newOpts = [...(it.options || [])];
                      newOpts[oi] = e.target.value;
                      updateItem(idx, { options: newOpts });
                    }}
                    placeholder={`선택지 ${oi + 1}`}
                    style={{ ...inp, flex: 1 }} />
                  <button onClick={() => {
                      const newOpts = (it.options || []).filter((_, i) => i !== oi);
                      updateItem(idx, { options: newOpts });
                    }} style={delBtn}>삭제</button>
                </div>
              ))}
              <button onClick={() => updateItem(idx, { options: [...(it.options || []), ''] })}
                style={{ ...addBtn, padding: '3px 10px', fontSize: T.xs }}>+ 선택지 추가</button>
            </div>
          )}
        </div>
      ))}
      {/* 자주 쓰는 항목 칩 줄은 뺐다 — 자리를 많이 차지해서(2026-10-28 지도사 「항목명 칠 때 밑에 자동 완성이면 충분」) · 항목명 칸 자동 완성(csc-item-lib)이 같은 일 */}
      <button onClick={addItem} style={addBtn}>+ 항목 추가</button>

      {/* 참고자료 */}
      <div style={{ marginTop: 12, padding: '8px 10px', borderRadius: R.sm, background: C.bgAlt, border: `0.5px solid ${C.border}` }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: C.sub, marginBottom: 6 }}>참고자료</div>
        {refLinks.map((link, idx) => (
          <div key={'l'+idx} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 3 }}>
            <span style={{ fontSize: 11, color: C.accent }}>🔗</span>
            <span style={{ flex: 1, fontSize: 11, color: C.accent, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link}</span>
            <button onClick={() => removeRefLink(idx)} style={{ background: 'none', border: 'none', fontSize: 11, color: C.muted, cursor: 'pointer', padding: '0 2px' }}>✕</button>
          </div>
        ))}
        {refFiles.map((f, idx) => (
          <div key={'f'+idx} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 3 }}>
            <span style={{ fontSize: 11, color: C.sub }}>📎</span>
            <span style={{ flex: 1, fontSize: 11, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
            <button onClick={() => removeRefFile(idx)} style={{ background: 'none', border: 'none', fontSize: 11, color: C.muted, cursor: 'pointer', padding: '0 2px' }}>✕</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input value={newLink} onChange={e => setNewLink(e.target.value)} placeholder="https://..." style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: `0.5px solid ${C.border}`, fontSize: 11, outline: 'none', background: C.white }}
            onKeyDown={e => { if (e.key === 'Enter') addRefLink(); }} />
          <button onClick={addRefLink} style={{ padding: '3px 8px', borderRadius: 4, border: `0.5px solid ${C.border}`, background: C.white, color: C.sub, fontSize: 11, cursor: 'pointer' }}>링크</button>
          <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={handleRefFile} />
          <button onClick={() => fileRef.current?.click()} style={{ padding: '3px 8px', borderRadius: 4, border: `0.5px solid ${C.border}`, background: C.white, color: C.sub, fontSize: 11, cursor: 'pointer' }}>파일</button>
        </div>
      </div>

      {/* 템플릿으로 저장 */}
      <div style={{ marginTop: 12 }}>
        {!saveMode ? (
          <button onClick={() => setSaveMode(true)}
            style={{ fontSize: T.xs, color: C.muted, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            💾 현재 항목을 템플릿으로 저장
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input value={saveName} onChange={e => setSaveName(e.target.value)} placeholder="템플릿 이름"
              onKeyDown={e => { if (e.key === 'Enter') handleSaveTemplate(); }}
              style={{ ...inp, flex: 1 }} />
            <button onClick={handleSaveTemplate}
              style={{ padding: '6px 14px', borderRadius: R.sm, border: 'none', background: C.accent, color: C.white, fontSize: T.xs, fontWeight: 600, cursor: 'pointer' }}>저장</button>
            <button onClick={() => { setSaveMode(false); setSaveName(''); }}
              style={{ padding: '6px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, color: C.muted, fontSize: T.xs, cursor: 'pointer' }}>취소</button>
          </div>
        )}
      </div>

      {/* V22_COLLECT_START:22-Ac — 자료수집 시작 (고객에게 전송) */}
      {onStartCollect && (value.items || []).length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          <button onClick={onStartCollect}
            style={{ width: '100%', padding: '11px 20px', borderRadius: R.md, border: 'none', background: C.accent, color: C.white, fontSize: T.sm, fontWeight: 700, cursor: 'pointer' }}>
            {startLabel || '▶ 자료수집 시작 (고객에게 전송)'}
          </button>
        </div>
      )}

      {/* ── 기존 프로젝트 연결 모달 ── */}
      {linkOpen && (
        <div onClick={() => { if (!linking) { setLinkOpen(false); setLinkSearch(''); } }}
          style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: 480, maxWidth: '92vw', maxHeight: '82vh', background: C.white, borderRadius: R.md, padding: 18, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontSize: T.md, fontWeight: 700, color: C.text }}>기존 자료수집 프로젝트 연결</span>
              <button onClick={() => { if (!linking) { setLinkOpen(false); setLinkSearch(''); } }}
                style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: C.muted, padding: 0, width: 24, height: 24 }}>✕</button>
            </div>
            <input value={linkSearch} onChange={e => setLinkSearch(e.target.value)}
              placeholder="프로젝트명·업체명 검색"
              style={{ ...inp, marginBottom: 10 }} autoFocus />
            <div style={{ flex: 1, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: R.sm }}>
              {myProjects.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: C.muted, fontSize: T.sm }}>등록된 프로젝트가 없습니다</div>
              )}
              {myProjects
                .filter(p => {
                  if (!linkSearch.trim()) return true;
                  const q = linkSearch.toLowerCase();
                  return (p.project||'').toLowerCase().includes(q) || (p.name||'').toLowerCase().includes(q) || (p.company_name||'').toLowerCase().includes(q);
                })
                .map(p => (
                  <div key={p.id} onClick={() => !linking && doLinkProject(p.id)}
                    style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}`, cursor: linking ? 'default' : 'pointer', opacity: linking ? 0.5 : 1 }}>
                    <div style={{ fontSize: T.sm, fontWeight: 600, color: C.text, marginBottom: 2 }}>{p.project || '(사업명 없음)'}</div>
                    <div style={{ fontSize: T.xs, color: C.muted }}>
                      {p.name ? p.name + ' · ' : ''}{p.status || '진행'}{(p.createdAt || p.created_at) ? ' · ' + String(p.createdAt || p.created_at).slice(0,10) : ''}
                    </div>
                  </div>
                ))}
            </div>
            <div style={{ fontSize: T.xs, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
              프로젝트를 선택하면 기존 수집 항목은 삭제되고 선택한 프로젝트의 항목으로 대체됩니다.
            </div>
          </div>
        </div>
      )}

      {bundlePid && <BundleLinkModal projectId={bundlePid} onClose={() => setBundlePid(null)} onToast={(m: string) => { if (onToast) onToast(m); }} />}

      {/* ── 포탈 미리보기 모달 ── */}
      {previewOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={() => setPreviewOpen(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
          <div style={{ position: 'relative', width: 375, maxHeight: '80vh', background: C.white, borderRadius: R.lg, overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: C.white, zIndex: 1 }}>
              <span style={{ fontSize: T.md, fontWeight: 700 }}>고객 포탈 미리보기</span>
              <button onClick={() => setPreviewOpen(false)} style={{ background: 'none', border: 'none', fontSize: T.lg, cursor: 'pointer', color: C.muted }}>✕</button>
            </div>
            <div style={{ padding: 16 }}>
              {/* 포탈 상단 헤더 영역 */}
              <div style={{ textAlign: 'center', padding: '16px 12px', marginBottom: 12, background: '#F8FAFC', borderRadius: R.md, border: `1px solid ${C.border}` }}>
                <div style={{ width: 48, height: 48, borderRadius: 10, background: C.accentLt, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', fontSize: 22 }}>📋</div>
                <div style={{ fontSize: T.sm, fontWeight: 700, color: C.text, marginBottom: 2 }}>자료수집 안내</div>
                <div style={{ fontSize: T.xs, color: C.muted }}>아래 항목을 확인하시고 제출해주세요</div>
              </div>
              {/* 참고자료 영역 */}
              {(refLinks.length > 0 || refFiles.length > 0) && (
                <div style={{ padding: '10px 12px', marginBottom: 12, background: '#FFFBEB', borderRadius: R.md, border: '1px solid #FDE68A' }}>
                  <div style={{ fontSize: T.xs, fontWeight: 700, color: '#92400E', marginBottom: 6 }}>📎 참고자료</div>
                  {refLinks.map((lk, i) => (
                    <div key={'l'+i} style={{ fontSize: T.xs, color: C.accent, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span>🔗</span> {typeof lk === 'string' ? lk : (lk as Record<string,string>).url || '링크'}
                    </div>
                  ))}
                  {refFiles.map((f, i) => (
                    <div key={'f'+i} style={{ fontSize: T.xs, color: C.sub, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span>📎</span> {f.name || '파일'}
                    </div>
                  ))}
                </div>
              )}
              {items.length === 0 && <div style={{ textAlign: 'center', color: C.muted, fontSize: T.sm, padding: 20 }}>수집 항목이 없습니다</div>}
              {items.map((it, idx) => (
                <div key={idx} style={{ padding: 12, borderRadius: R.md, border: `1px solid ${C.border}`, marginBottom: 8, background: C.bgAlt }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                    <span style={{ fontSize: T.sm, fontWeight: 600, color: C.text }}>{it.label || '(항목명 없음)'}</span>
                    {it.required && <span style={{ color: C.red, fontWeight: 700 }}>*</span>}
                    {it.sensitive && <span style={{ fontSize: T.xs, background: '#D97706', color: '#fff', padding: '1px 4px', borderRadius: 3, fontWeight: 600 }}>민감</span>}
                  </div>
                  <span style={{ fontSize: T.xs, color: C.accent, background: C.accentLt, padding: '1px 6px', borderRadius: 4 }}>
                    {it.type === 'file' ? '📎 파일' : it.type === 'text_long' ? '✏️ 장문' : it.type === 'radio' ? '◉ 객관식' : it.type === 'checkbox' ? '☑ 다중선택' : '✏️ 단문'}
                  </span>
                  {it.description && <div style={{ fontSize: T.xs, color: C.sub, marginTop: 4, whiteSpace: 'pre-wrap' }}>{it.description}</div>}
                  {(it.guides || []).map((g, gi) => (
                    <div key={gi} style={{ fontSize: T.xs, color: g.type === 'link' ? C.accent : C.sub, marginTop: 2 }}>
                      {g.type === 'link' ? `🔗 ${g.label || g.url}` : `📎 ${g.label || g.fileName}`}
                    </div>
                  ))}
                  {needsOptions(it.type) && (it.options || []).length > 0 ? (
                    <div style={{ marginTop: 6 }}>
                      {(it.options || []).map((opt, oi) => (
                        <div key={oi} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', marginBottom: 2, borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, fontSize: T.xs, color: C.text }}>
                          <span style={{ color: C.muted }}>{it.type === 'radio' ? '○' : '□'}</span>
                          {opt || `선택지 ${oi + 1}`}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ marginTop: 6, padding: '10px 12px', borderRadius: R.sm, border: `1.5px dashed ${C.border}`, background: C.white, textAlign: 'center', color: C.muted, fontSize: T.xs }}>
                      {it.type === 'file' ? '클릭하여 파일 업로드' : '텍스트 입력란'}
                    </div>
                  )}
                </div>
              ))}
              <div style={{ textAlign: 'center', fontSize: T.xs, color: C.muted, marginTop: 16, padding: '12px 0', borderTop: `1px solid ${C.border}` }}>
                실제 고객은 이 화면에서 자료를 제출합니다
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
